import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { empSkillService } from '@/services/empSkillService'
import { employeesService } from '@/services/employeesService'
import type { SkillVm, SkillTypeOption } from '@/api/types/empSkill.types'
import { showError, showSuccess } from '@/lib/swal'

export const EmpSkillEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const ts = (key: string) => t(`empSkill.${key}`)
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')
  const listEmpId = useMemo(() => {
    const n = Number(searchParams.get('empId') ?? searchParams.get('EmpId') ?? 0)
    return n > 0 && !Number.isNaN(n) ? n : 0
  }, [searchParams])
  const backToList = useCallback(() => {
    navigate(listEmpId ? `/skills?empId=${listEmpId}` : '/skills')
  }, [navigate, listEmpId])

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<{ id?: number; empCode?: string; empFirstNameEn?: string; empSecondNameEn?: string; empLastNameEn?: string }[]>([])
  const [skillTypes, setSkillTypes] = useState<SkillTypeOption[]>([])

  const [form, setForm] = useState({
    empId: 0,
    skillTypeId: 0,
    scoreOutOf100: '' as string | number,
    experienceYear: '' as string | number,
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        const data = (res as any)?.data ?? (res as any)?.Data ?? res
        setEmployees(Array.isArray(data) ? data : [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    if (!isEditing && listEmpId) {
      setForm((prev) => (prev.empId === listEmpId ? prev : { ...prev, empId: listEmpId }))
    }
  }, [isEditing, listEmpId])

  useEffect(() => {
    const loadSkillTypes = async () => {
      try {
        const types = await empSkillService.getSkillTypes()
        setSkillTypes(types ?? [])
      } catch {
        setSkillTypes([])
      }
    }
    loadSkillTypes()
  }, [])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await empSkillService.getById(Number(id))
        setForm({
          empId: data.empId ?? 0,
          skillTypeId: (data.skillTypeId ?? data.empSkillTypeId) ?? 0,
          scoreOutOf100: (data.scoreOutOf100 ?? data.empScoreOutOf100) ?? '',
          experienceYear: (data.experienceYear ?? data.empExperienceYear) ?? '',
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? ts('loadFailed'))
        backToList()
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, backToList])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
        const label =
          fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? t('empSkill.employeeFallback', { id: e.id ?? 0 })
        return { value: String(e.id ?? 0), label }
      }).filter((o) => o.value !== '0'),
    [employees, t]
  )

  const skillTypeOptions = useMemo(
    () =>
      skillTypes.map((st) => {
        const name =
          (st as any).lookupNameEn ??
          (st as any).lookupNameAr ??
          (st as any).name ??
          t('empSkill.typeFallback', { id: st.id ?? 0 })
        return { value: String(st.id ?? 0), label: name }
      }),
    [skillTypes, t]
  )

  const employeeSelectLocked = isEditing || listEmpId > 0
  const displayEmpCode = useMemo(() => {
    const emp = employees.find((e) => e.id === form.empId)
    return emp?.empCode?.trim() || '—'
  }, [employees, form.empId])

  const handleChange = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error(ts('branchRequired'))
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error(ts('employeeRequired'))
      return
    }
    const score = typeof form.scoreOutOf100 === 'number' ? form.scoreOutOf100 : parseInt(String(form.scoreOutOf100), 10)
    if (!Number.isFinite(score) || score < 0 || score > 100) {
      toast.error(ts('scoreInvalid'))
      return
    }
    const expYear = typeof form.experienceYear === 'number' ? form.experienceYear : parseInt(String(form.experienceYear), 10)
    if (!Number.isFinite(expYear) || expYear < 0) {
      toast.error(ts('experienceYearInvalid'))
      return
    }

    const payload: SkillVm = {
      id: isEditing && id ? Number(id) : 0,
      empId: form.empId,
      // Frontend field names
      skillTypeId: form.skillTypeId,
      scoreOutOf100: score,
      experienceYear: expYear,
      // Backend field names (Swagger sample)
      empSkillTypeId: form.skillTypeId,
      empScoreOutOf100: score,
      empExperienceYear: expYear,
      branchId,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await empSkillService.update(payload)
        await showSuccess(ts('updatedTitle'), ts('updatedMessage'))
      } else {
        await empSkillService.save(payload)
        await showSuccess(ts('savedTitle'), ts('savedMessage'))
      }
      backToList()
    } catch (e: any) {
      await showError(ts('saveFailedTitle'), e?.response?.data?.message ?? e?.message ?? ts('saveFailedMessage'))
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={ts('formTitle')} description={ts('formDescriptionGeneric')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">{ts('selectBranchFirst')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={ts('formTitle')}
        description={isEditing ? ts('formDescriptionEdit') : ts('formDescriptionAdd')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{ts('cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="emp-code-display">{ts('labelEmpCode')}</Label>
              <Input id="emp-code-display" readOnly value={displayEmpCode} className="bg-muted/50" aria-readonly />
            </div>
            <div className="space-y-2">
              <Label>
                {ts('requiredMark')} {ts('labelEmployee')}
              </Label>
              <SearchableSelect
                options={[{ value: '0', label: ts('placeholderSelectEmployee') }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => {
                  if (employeeSelectLocked) return
                  handleChange({ empId: Number(v) || 0 })
                }}
                placeholder={ts('placeholderSelectEmployee')}
                searchPlaceholder={ts('searchEmployeePlaceholder')}
                emptyMessage={ts('emptyEmployee')}
                disabled={employeeSelectLocked}
              />
            </div>
            <div className="space-y-2">
              <Label>{ts('labelSkillType')}</Label>
              <SearchableSelect
                options={[{ value: '0', label: ts('placeholderSelectSkillType') }, ...skillTypeOptions]}
                value={form.skillTypeId ? String(form.skillTypeId) : '0'}
                onValueChange={(v) => handleChange({ skillTypeId: Number(v) || 0 })}
                placeholder={ts('placeholderSkillType')}
                searchPlaceholder={ts('searchSkillTypePlaceholder')}
                emptyMessage={ts('emptySkillType')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {ts('requiredMark')} {ts('labelScoreOutOf100')}
              </Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={form.scoreOutOf100 === '' ? '' : form.scoreOutOf100}
                onChange={(e) => handleChange({ scoreOutOf100: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder={ts('placeholderScore')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {ts('requiredMark')} {ts('labelExperienceYear')}
              </Label>
              <Input
                type="number"
                min={0}
                value={form.experienceYear === '' ? '' : form.experienceYear}
                onChange={(e) => handleChange({ experienceYear: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder={ts('placeholderExperienceYear')}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => backToList()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {ts('return')}
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {isEditing ? t('common.update') : t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
